import React from "react";
import { useTranslation } from "react-i18next";

import { useSimulation } from "./SimulationContext";
import { useTheme } from "../common/ThemeContext";
import { UserX } from "lucide-react";
import TrainabilityMiniChart from "./TrainabilityMiniChart";
import {
  getTrainabilityScore10,
  hasTrainability,
  getTrainabilityDisplayColor,
  resolveGlobalTrainabilityLevel,
} from "../common/TrainabilityUtils";

const PlayerCard = ({ player, isSimulating, onFileDrop, onCardClick, trainabilityData }) => {
  const { t } = useTranslation();
  const { simulations } = useSimulation();
  const { isDarkMode } = useTheme();

  // ⬅️ NEW : joueur inactif ?
  const inactive =
    player?.is_active === false || player?.isActive === false;

  const playerSimulations = Object.values(simulations || {}).filter(
    (sim) => sim.player_id === player.id
  );
  
  const ongoingSimulation = playerSimulations.find((sim) => sim.status === "in_progress");
  const progressData = ongoingSimulation
    ? ongoingSimulation
    : playerSimulations.length > 0
      ? playerSimulations.reduce((minSim, sim) => sim.progress < minSim.progress ? sim : minSim, playerSimulations[0])
      : { progress: 0, status: "idle" };
  
  const progress = progressData.progress;
  const isInQueue = progressData.status === "queued";
 
  const intensityValue =
    player.intensity_index !== null && player.intensity_index !== undefined
      ? player.intensity_index
      : null;

  const lowerBodyIntensity = intensityValue !== null
    ? `${intensityValue} PT`
    : t("common.na");
  const lowerBodyIntensityColor = intensityValue ?? 0;

  const loadValue =
    player.mechanical_load_index !== null && player.mechanical_load_index !== undefined
      ? player.mechanical_load_index
      : null;

  const lowerBodyMechanicalLoad = loadValue !== null
    ? `${loadValue} PT`
    : t("common.na");
  const lowerBodyMechanicalLoadColor = loadValue ?? 0;

  const fatigueValue =
    player.mechanical_fatigue !== null && player.mechanical_fatigue !== undefined
      ? player.mechanical_fatigue * 100
      : null;

  const lowerBodyMechanicalFatigue = fatigueValue !== null
    ? `${fatigueValue.toFixed(0)}%`
    : t("common.na");
  const lowerBodyMechanicalFatigueColor = fatigueValue ?? 0;

  const power = player.power_wkg !== null && player.power_wkg !== undefined
    ? `${player.power_wkg.toFixed(1)} W/kg`
    : t("common.na");

  // et powerColor basé sur player.power_wkg
  let powerColor = "#EF4444";
  if (player.power_wkg != null) {
    if (player.power_wkg >= 8) powerColor = "#10B981";
    else if (player.power_wkg >= 6) powerColor = "#FACC15";
  }

  // Dernière valeur réelle de trainabilité affichable
  const lastRealPoint =
    trainabilityData?.real?.length > 0
      ? [...trainabilityData.real]
          .filter((point) => hasTrainability(point?.trainability))
          .sort((a, b) => a.date.localeCompare(b.date))
          .at(-1)
      : null;

  const chartTrainability = getTrainabilityScore10(lastRealPoint?.trainability);
  const playerTrainability = getTrainabilityScore10(player.trainability_index);
  const displayTrainability = hasTrainability(chartTrainability)
    ? chartTrainability
    : playerTrainability;

  const hasVisibleTrainability = hasTrainability(displayTrainability);

  const hasVisibleTrainabilityChart =
    Boolean(trainabilityData) &&
    (
      trainabilityData?.real?.some((point) =>
        hasTrainability(point?.trainability)
      ) ||
      trainabilityData?.scenarios?.some((scenario) =>
        scenario?.points?.some((point) =>
          hasTrainability(point?.trainability)
        )
      )
    );

  const trainabilityLevel = resolveGlobalTrainabilityLevel(player.trainability_level);
  const trainabilityColor = getTrainabilityDisplayColor({
    score: displayTrainability,
    level: trainabilityLevel,
    forText: true,
  });
  
  const getColorFromThresholds = (value, type) => {
    if (value === null || value === undefined) return "#AAAAAA"; // Neutre si pas de valeur

    const val = parseFloat(value);

    switch (type) {
      case "intensity_index":
        if (val < 1) return "#3B82F6";       // 🔵 BLEU
        if (val > 7) return "#DC2626";       // 🔴 ROUGE
        if (val > 4.4) return "#F97316";     // 🟠 ORANGE
        return "#10B981";                    // ✅ NORMAL

      case "mechanical_load_index":
        if (val < 1) return "#3B82F6";
        if (val > 7) return "#DC2626";
        if (val > 4.4) return "#F97316";
        return "#10B981";

      case "fatigue":
        if (val >= 75) return "#DC2626";
        if (val >= 65) return "#F97316";
        return "#10B981";

      default:
        return "#10B981";
    }
  };

  return (
    <div
      className={`relative w-64 mt-10 bg-cover bg-${isDarkMode ? "gray-700" : "gray-500"} bg-center shadow-lg rounded-lg p-2 ${
        isSimulating ? "bg-gray-400" : "cursor-pointer"
      }`}
      onClick={!isSimulating ? onCardClick : undefined}
    >
      {isSimulating && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
          <p className="text-white font-semibold mb-2">{t("player_card.simulation_in_progress")}</p>
          <div className="w-32 h-2 bg-gray-300 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500" style={{ width: `${progress}%` }}></div>
          </div>
          <p className="text-white text-xs mt-1">{progress}%</p>
        </div>
      )}

      {isInQueue && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
          <p className="text-white font-semibold mb-2">{t("player_card.in_queue")}</p>
        </div>
      )}

      <div className="flex items-end justify-between px-1">
        {/* ⬅️ AVATAR légèrement à gauche + BADGE INACTIF */}
        <div className="relative w-20 h-20 -mt-9 rounded-xl overflow-hidden bg-gradient-to-r from-gray-400 to-blue-500 flex items-center justify-center text-white ml-1">
          {player.profile_picture ? (
            <img
              src={`data:image/jpeg;base64,${player.profile_picture}`}
              alt={`${player.first_name} ${player.last_name}`}
              className="object-cover w-full h-full"
            />
          ) : (
            `${player.first_name.charAt(0)}${player.last_name.charAt(0)}`
          )}

          {inactive && (
            <div
              className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-red-600 text-white flex items-center justify-center shadow-md ring-2 ring-white"
              title={t("player_card.inactive")}
            >
              <UserX className="h-3.5 w-3.5" />
            </div>
          )}
        </div>

        {/* Trainabilité réelle (dernière simulation) – affiché en grand à droite */}
        {hasVisibleTrainability && (
          <div className="text-right mr-2 mb-1">
            <div
              className="text-3xl font-black leading-none"
              style={{ color: trainabilityColor }}
            >
              {displayTrainability.toFixed(1)}
            </div>
            <div className="text-[9px] uppercase tracking-wider text-gray-400 mt-0.5">
              {t("metrics.trainability")}
            </div>
          </div>
        )}
      </div>

      <h3 className="text-center text-sm font-bold mt-3 text-white">
        {player.first_name} {player.last_name}
      </h3>

      <div className="text-center text-sm text-white">
        <p>{t("player_card.age")} : {player.age || t("common.na")}</p>
      </div>

      {/* Trainability 14-day chart */}
      {hasVisibleTrainabilityChart && (
        <div className="mt-2 px-1">
          <p
            className="text-[10px] text-gray-400 font-semibold mb-0.5 uppercase tracking-wide"
            style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.8)" }}
          >
            {t("player_card.trainability_14d")}
          </p>
          <TrainabilityMiniChart
            realPoints={trainabilityData.real || []}
            scenarios={trainabilityData.scenarios || []}
            isDarkMode={isDarkMode}
            height={70}
            todayDate={trainabilityData.todayDate}
          />
          {(trainabilityData.scenarios || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-0.5">
              <span className="flex items-center gap-1 text-[8px] text-gray-400">
                <span className="inline-block w-3 h-0.5 bg-emerald-500 rounded" /> {t("player_card.real")}
              </span>
              {trainabilityData.scenarios.slice(0, 3).map((sc, i) => (
                <span key={i} className="flex items-center gap-1 text-[8px] text-gray-400">
                  <span
                    className="inline-block w-3 h-0.5 rounded"
                    style={{ background: ["#6366f1", "#f59e0b", "#06b6d4"][i] }}
                  />
                  {(sc.name || t("player_card.scenario_short", { n: i + 1 })).replace(/^scenario_/, "").slice(0, 12)}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-1 text-white">
        <div className="flex">
          {/* Labels */}
          <div
            className="text-sm text-gray-300 font-semibold"
            style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.8)" }}
          >
            <p className="mb-1 mt-1">{t("player_card.last_simulation")}</p>
            <p className="mb-1 mt-2">{t("stat_cards.mechanical_load")}:</p>
            <p className="mb-1 mt-2">{t("stat_cards.intensity")}:</p>
            <p className="mb-1 mt-2">{t("stat_cards.mechanical_fatigue")}:</p>
            {/* <p className="mb-1 mt-2">Power:</p> */}
          </div>

          {/* Valeurs */}
          <div>
            <p
              className="text-xs font-bold mb-1 mt-2 ml-1"
              style={{
                color: player.timeinfo_date ? "#FFFFFF" : "#AAAAAA",
                textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
              }}
            >
              {player.timeinfo_date || t("common.na")}
            </p>

            <p
              className="text-sm font-bold mb-1 mt-2 ml-2"
              style={{
                color: getColorFromThresholds(
                  lowerBodyMechanicalLoadColor,
                  "mechanical_load_index"
                ),
                textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
              }}
            >
              {lowerBodyMechanicalLoad || t("common.na")}
            </p>

            <p
              className="text-sm font-bold mb-1 mt-2 ml-2"
              style={{
                color: getColorFromThresholds(
                  lowerBodyIntensityColor,
                  "intensity_index"
                ),
                textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
              }}
            >
              {lowerBodyIntensity || t("common.na")}
            </p>

            <p
              className="text-sm font-bold mb-1 mt-2 ml-2"
              style={{
                color: getColorFromThresholds(
                  lowerBodyMechanicalFatigueColor,
                  "fatigue"
                ),
                textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
              }}
            >
              {lowerBodyMechanicalFatigue || t("common.na")}
            </p>

            {/* <p
              className="text-sm font-bold mb-1 mt-2 ml-2"
              style={{
                color: powerColor,
                textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
              }}
            >
              {power || "N/A"}
            </p> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerCard;
